const PREVIEW_MAX_EDGE = 800;
const PREVIEW_JPEG_QUALITY = 0.72;

const IMAGE_PREVIEW_EXTENSIONS = new Set([
  ".png",
  ".jpeg",
  ".jpg",
  ".jfif",
  ".webp",
  ".gif",
  ".bmp",
  ".svg",
]);

function getExtension(fileName: string): string {
  const lastDot = fileName.lastIndexOf(".");
  if (lastDot < 0) {
    return "";
  }
  return fileName.slice(lastDot).toLowerCase();
}

function canvasToJpegBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => resolve(blob),
      "image/jpeg",
      PREVIEW_JPEG_QUALITY,
    );
  });
}

function drawImageToCanvas(image: CanvasImageSource, width: number, height: number): HTMLCanvasElement {
  const scale = Math.min(PREVIEW_MAX_EDGE / width, PREVIEW_MAX_EDGE / height, 1);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Could not create preview canvas.");
  }
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas;
}

async function createImagePreview(file: File): Promise<Blob | null> {
  const objectUrl = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.crossOrigin = "anonymous";
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error("Could not read image file."));
      image.src = objectUrl;
    });
    const canvas = drawImageToCanvas(
      image,
      image.naturalWidth || image.width,
      image.naturalHeight || image.height,
    );
    return canvasToJpegBlob(canvas);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function createPdfPreview(file: File): Promise<Blob | null> {
  const pdfjs = await import("pdfjs-dist");
  if (!pdfjs.GlobalWorkerOptions.workerSrc) {
    pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
  }

  const data = new Uint8Array(await file.arrayBuffer());
  const pdf = await pdfjs.getDocument({ data }).promise;
  const page = await pdf.getPage(1);
  const baseViewport = page.getViewport({ scale: 1 });
  const scale = Math.min(
    PREVIEW_MAX_EDGE / baseViewport.width,
    PREVIEW_MAX_EDGE / baseViewport.height,
    1.5,
  );
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(viewport.width));
  canvas.height = Math.max(1, Math.round(viewport.height));
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Could not create PDF preview canvas.");
  }
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({
    canvasContext: ctx,
    viewport,
  }).promise;
  return canvasToJpegBlob(canvas);
}

/**
 * Builds a JPEG thumbnail in the browser so production does not depend on
 * Magick.NET / Ghostscript on the Cloud Files API container.
 */
export async function createFilePreviewBlob(file: File): Promise<Blob | null> {
  if (typeof window === "undefined" || !file) {
    return null;
  }

  const extension = getExtension(file.name);
  try {
    if (extension === ".pdf" || file.type === "application/pdf") {
      return await createPdfPreview(file);
    }
    if (IMAGE_PREVIEW_EXTENSIONS.has(extension) || file.type.startsWith("image/")) {
      return await createImagePreview(file);
    }
    return null;
  } catch {
    return null;
  }
}

export async function createFilePreviewFile(file: File): Promise<File | null> {
  const blob = await createFilePreviewBlob(file);
  if (!blob) {
    return null;
  }
  const baseName = file.name.replace(/\.[^/.]+$/, "") || "preview";
  return new File([blob], `${baseName}-preview.jpg`, { type: "image/jpeg" });
}
